/* ============================================================
   MAP THE AIR — "Night Observatory" design system
   Dark, cinematic, instrument-panel aesthetic.
   ============================================================ */

:root {
  /* Canvas */
  --bg: #060a12;
  --bg-deep: #04070d;
  --panel: rgba(12, 18, 32, 0.82);
  --panel-solid: #0c1220;
  --panel-raised: rgba(20, 28, 46, 0.9);
  --hairline: rgba(148, 163, 197, 0.14);
  --hairline-strong: rgba(148, 163, 197, 0.28);

  /* Ink */
  --text: #eaf0fb;
  --text-dim: #97a3bd;
  --text-faint: #5f6c88;

  /* Signal */
  --live: #5eead4;
  --focus: #7dd3fc;

  /* AQI spectrum (dark-optimized) */
  --aqi-good: #45d6a1;
  --aqi-moderate: #f5d445;
  --aqi-usg: #f79b4c;
  --aqi-unhealthy: #f4626f;
  --aqi-very: #b07ef2;
  --aqi-hazardous: #cf3657;
  --aqi-unknown: #64748b;

  /* Type */
  --font-display:
    'Bricolage Grotesque', 'Avenir Next', 'Segoe UI', system-ui, sans-serif;
  --font-mono: 'IBM Plex Mono', ui-monospace, 'SF Mono', Menlo, monospace;

  /* Shape + depth */
  --radius-lg: 18px;
  --radius-md: 12px;
  --radius-sm: 8px;
  --shadow-panel: 0 24px 64px rgba(2, 6, 16, 0.55);
  --shadow-pop: 0 12px 36px rgba(2, 6, 16, 0.65);
  --blur-panel: saturate(1.3) blur(18px);
}

* {
  box-sizing: border-box;
}

html,
body,
#root {
  height: 100%;
  margin: 0;
  padding: 0;
}

body {
  overflow: hidden;
  background:
    radial-gradient(
      1200px 800px at 75% -10%,
      rgba(45, 84, 128, 0.18),
      transparent 60%
    ),
    var(--bg);
  color: var(--text);
  font-family: var(--font-display);
  font-size: 15px;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

::selection {
  background: rgba(94, 234, 212, 0.28);
}

button {
  font-family: inherit;
  color: inherit;
}

/* Slim, dark scrollbars */
* {
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 163, 197, 0.28) transparent;
}
*::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
*::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 197, 0.24);
  border-radius: 99px;
}
*::-webkit-scrollbar-track {
  background: transparent;
}

/* ============================================================
   App shell
   ============================================================ */

.app {
  position: relative;
  height: 100dvh;
  overflow: hidden;
}

.app::after {
  /* Fine grain to kill banding on gradients */
  content: '';
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 4000;
  opacity: 0.05;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='120' height='120' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E");
}

.view-layer {
  position: absolute;
  inset: 0;
  /* Own stacking context so map controls can't paint over other views */
  isolation: isolate;
}

.view-layer[data-hidden='true'] {
  visibility: hidden;
  pointer-events: none;
  z-index: -1;
}

/* ============================================================
   Panels, buttons, inputs — shared primitives
   ============================================================ */

.glass {
  background: var(--panel);
  border: 1px solid var(--hairline);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-panel);
  backdrop-filter: var(--blur-panel);
  -webkit-backdrop-filter: var(--blur-panel);
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: 1px solid var(--hairline);
  background: rgba(20, 28, 46, 0.6);
  color: var(--text);
  border-radius: var(--radius-sm);
  padding: 8px 13px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition:
    background 0.18s ease,
    border-color 0.18s ease,
    transform 0.18s ease;
}

.btn:hover {
  background: rgba(36, 48, 74, 0.75);
  border-color: var(--hairline-strong);
}

.btn:active {
  transform: scale(0.97);
}

.btn:focus-visible,
.seg button:focus-visible,
.icon-btn:focus-visible,
input:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
}

.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex: 0 0 auto;
  border: 1px solid var(--hairline);
  border-radius: var(--radius-sm);
  background: rgba(20, 28, 46, 0.6);
  color: var(--text-dim);
  cursor: pointer;
  transition:
    background 0.18s ease,
    color 0.18s ease,
    border-color 0.18s ease;
}

.icon-btn:hover {
  background: rgba(36, 48, 74, 0.75);
  color: var(--text);
}

.icon-btn svg {
  width: 17px;
  height: 17px;
}

/* Segmented control */
.seg {
  display: inline-flex;
  padding: 3px;
  gap: 2px;
  border: 1px solid var(--hairline);
  border-radius: 10px;
  background: rgba(8, 12, 22, 0.72);
}

.seg button {
  border: 0;
  background: transparent;
  color: var(--text-dim);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.01em;
  padding: 6px 14px;
  border-radius: 7px;
  cursor: pointer;
  transition:
    background 0.18s ease,
    color 0.18s ease;
}

.seg button[data-active='true'] {
  background: rgba(94, 234, 212, 0.14);
  color: var(--live);
}

.seg button:hover:not([data-active='true']) {
  color: var(--text);
}

/* Text input */
.field {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--hairline);
  border-radius: var(--radius-sm);
  background: rgba(8, 12, 22, 0.72);
  padding: 0 10px;
  height: 36px;
  transition: border-color 0.18s ease;
}

.field:focus-within {
  border-color: rgba(125, 211, 252, 0.55);
}

.field svg {
  width: 15px;
  height: 15px;
  color: var(--text-faint);
  flex: 0 0 auto;
}

.field input {
  border: 0;
  background: transparent;
  color: var(--text);
  font-family: var(--font-display);
  font-size: 13px;
  width: 100%;
  outline: none;
}

.field input::placeholder {
  color: var(--text-faint);
}

/* Mono readout */
.mono {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
}

.kicker {
  font-family: var(--font-mono);
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--text-faint);
}

/* AQI value chip */
.aqi-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 46px;
  padding: 3px 8px;
  border-radius: 7px;
  font-family: var(--font-mono);
  font-size: 13px;
  font-weight: 600;
  color: #081018;
}

/* ============================================================
   Top bar
   ============================================================ */

.topbar {
  position: fixed;
  top: 14px;
  left: 14px;
  right: 14px;
  z-index: 1200;
  display: flex;
  align-items: center;
  gap: 12px;
  pointer-events: none;
}

.topbar > * {
  pointer-events: auto;
}

.brand {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 8px 14px 8px 11px;
}

.brand-pulse {
  position: relative;
  width: 10px;
  height: 10px;
  flex: 0 0 auto;
}

.brand-pulse::before,
.brand-pulse::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: var(--live);
}

.brand-pulse::after {
  animation: pulse-ring 2.6s cubic-bezier(0.2, 0.6, 0.4, 1) infinite;
}

@keyframes pulse-ring {
  0% {
    transform: scale(1);
    opacity: 0.7;
  }
  80%,
  100% {
    transform: scale(2.7);
    opacity: 0;
  }
}

.brand-name {
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.1;
  white-space: nowrap;
}

.brand-name em {
  font-style: normal;
  font-weight: 300;
  color: var(--text-dim);
}

.brand-sub {
  font-family: var(--font-mono);
  font-size: 9.5px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-faint);
  white-space: nowrap;
}

.topbar-spacer {
  flex: 1;
}

.topbar-cluster {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px;
}

.station-ticker {
  display: none;
  align-items: baseline;
  gap: 6px;
  padding: 0 10px;
  white-space: nowrap;
}

.station-ticker strong {
  font-family: var(--font-mono);
  font-size: 14px;
  font-weight: 600;
  color: var(--live);
}

.station-ticker span {
  font-size: 11.5px;
  color: var(--text-dim);
}

@media (min-width: 900px) {
  .station-ticker {
    display: inline-flex;
  }
}

@media (max-width: 700px) {
  .topbar {
    top: 10px;
    left: 10px;
    right: 10px;
    gap: 8px;
  }
  .brand {
    padding: 7px 11px 7px 9px;
  }
  .brand-sub {
    display: none;
  }
  .brand-name {
    font-size: 15px;
  }
}

/* ============================================================
   Toasts / notices
   ============================================================ */

.notice {
  position: fixed;
  top: 72px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1600;
  max-width: min(520px, calc(100vw - 32px));
  padding: 10px 16px;
  border-radius: var(--radius-md);
  border: 1px solid rgba(244, 98, 111, 0.35);
  background: rgba(40, 14, 22, 0.88);
  color: #ffd7db;
  font-size: 13px;
  backdrop-filter: var(--blur-panel);
  -webkit-backdrop-filter: var(--blur-panel);
  box-shadow: var(--shadow-pop);
  animation: notice-in 0.35s cubic-bezier(0.2, 0.9, 0.3, 1.2);
}

@keyframes notice-in {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

/* ============================================================
   Loading screen
   ============================================================ */

.loading-screen {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: grid;
  place-items: center;
  background:
    radial-gradient(
      900px 600px at 50% 40%,
      rgba(45, 84, 128, 0.2),
      transparent 65%
    ),
    var(--bg-deep);
  transition:
    opacity 0.6s ease,
    visibility 0.6s ease;
}

.loading-screen[data-done='true'] {
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
}

.loading-inner {
  display: grid;
  justify-items: center;
  gap: 26px;
}

.orbit {
  position: relative;
  width: 130px;
  height: 130px;
}

.orbit i {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 1px solid rgba(94, 234, 212, 0.35);
  animation: orbit-breathe 2.8s ease-in-out infinite;
}

.orbit i:nth-child(2) {
  inset: 18px;
  animation-delay: 0.35s;
  border-color: rgba(125, 211, 252, 0.3);
}

.orbit i:nth-child(3) {
  inset: 36px;
  animation-delay: 0.7s;
  border-color: rgba(176, 126, 242, 0.28);
}

.orbit em {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 8px;
  height: 8px;
  margin: -4px;
  border-radius: 50%;
  background: var(--live);
  box-shadow: 0 0 22px 4px rgba(94, 234, 212, 0.55);
}

@keyframes orbit-breathe {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.85;
  }
  50% {
    transform: scale(1.12);
    opacity: 0.3;
  }
}

.loading-word {
  font-size: 21px;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.loading-word em {
  font-style: normal;
  font-weight: 300;
  color: var(--text-dim);
}

.loading-status {
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--text-faint);
  animation: status-blink 1.6s ease-in-out infinite;
}

@keyframes status-blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.45;
  }
}

/* ============================================================
   Map view
   ============================================================ */

.map-canvas,
.map-canvas.maplibregl-map {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.map-canvas .maplibregl-canvas {
  outline: none;
}

/* Vignette so panels sit on calmer edges */
.map-vignette {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 1;
  background:
    linear-gradient(180deg, rgba(4, 7, 13, 0.62), transparent 18%),
    linear-gradient(0deg, rgba(4, 7, 13, 0.55), transparent 22%);
}

.map-fallback {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  text-align: center;
  padding: 32px;
}

.map-fallback h2 {
  margin: 0 0 8px;
  font-size: 20px;
}

.map-fallback p {
  margin: 0;
  color: var(--text-dim);
  max-width: 420px;
}

/* MapLibre chrome overrides */
.maplibregl-popup .maplibregl-popup-content {
  background: var(--panel-raised);
  color: var(--text);
  border: 1px solid var(--hairline-strong);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-pop);
  padding: 12px 14px;
  font-family: var(--font-display);
  backdrop-filter: var(--blur-panel);
  -webkit-backdrop-filter: var(--blur-panel);
}

.maplibregl-popup-anchor-bottom .maplibregl-popup-tip {
  border-top-color: var(--panel-raised);
}
.maplibregl-popup-anchor-top .maplibregl-popup-tip {
  border-bottom-color: var(--panel-raised);
}
.maplibregl-popup-anchor-left .maplibregl-popup-tip {
  border-right-color: var(--panel-raised);
}
.maplibregl-popup-anchor-right .maplibregl-popup-tip {
  border-left-color: var(--panel-raised);
}

.maplibregl-ctrl-group {
  background: var(--panel) !important;
  border: 1px solid var(--hairline);
  border-radius: 10px !important;
  overflow: hidden;
  box-shadow: var(--shadow-pop) !important;
}

.maplibregl-ctrl-group button {
  background: transparent !important;
  border: 0 !important;
}

.maplibregl-ctrl-group button + button {
  border-top: 1px solid var(--hairline) !important;
}

.maplibregl-ctrl button .maplibregl-ctrl-icon {
  filter: invert(0.9) hue-rotate(180deg);
}

.maplibregl-ctrl-attrib {
  background: rgba(6, 10, 18, 0.72) !important;
  border-radius: 8px 0 0 0;
  font-size: 10px;
}

.maplibregl-ctrl-attrib,
.maplibregl-ctrl-attrib a {
  color: var(--text-faint) !important;
}

.maplibregl-ctrl-scale {
  background: rgba(6, 10, 18, 0.6);
  border-color: rgba(148, 163, 197, 0.4) !important;
  color: var(--text-dim);
  font-family: var(--font-mono);
  font-size: 10px;
}

/* Station hover popup body */
.stat-pop {
  min-width: 190px;
  max-width: 250px;
}

.stat-pop-name {
  font-weight: 700;
  font-size: 13.5px;
  line-height: 1.3;
  margin-bottom: 7px;
}

.stat-pop-row {
  display: flex;
  align-items: center;
  gap: 9px;
}

.stat-pop-aqi {
  font-family: var(--font-mono);
  font-size: 22px;
  font-weight: 600;
  line-height: 1;
}

.stat-pop-cat {
  font-size: 11.5px;
  font-weight: 600;
  line-height: 1.25;
}

.stat-pop-meta {
  margin-top: 8px;
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--text-faint);
}

.stat-pop-hint {
  margin-top: 6px;
  font-size: 10.5px;
  color: var(--text-faint);
}

/* Mode toggle + legend dock (bottom center) */
.map-dock {
  position: absolute;
  left: 50%;
  bottom: 26px;
  transform: translateX(-50%);
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  pointer-events: none;
}

.map-dock > * {
  pointer-events: auto;
}

.legend-strip {
  display: flex;
  align-items: center;
  gap: 0;
  padding: 8px 12px;
  border-radius: 999px;
}

.legend-cell {
  position: relative;
  display: grid;
  justify-items: center;
  gap: 5px;
  padding: 0 1px;
  cursor: default;
}

.legend-cell i {
  display: block;
  width: 44px;
  height: 6px;
  background: var(--cell-color);
}

.legend-cell:first-child i {
  border-radius: 99px 0 0 99px;
}

.legend-cell:last-child i {
  border-radius: 0 99px 99px 0;
}

.legend-cell span {
  font-family: var(--font-mono);
  font-size: 9px;
  letter-spacing: 0.06em;
  color: var(--text-faint);
  white-space: nowrap;
}

.legend-cell:hover span {
  color: var(--text-dim);
}

.legend-title {
  font-family: var(--font-mono);
  font-size: 9px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--text-faint);
  margin-right: 10px;
}

@media (max-width: 700px) {
  .map-dock {
    bottom: 18px;
    width: calc(100vw - 20px);
  }
  .legend-cell i {
    width: 100%;
    min-width: 30px;
  }
  .legend-cell {
    flex: 1;
  }
  .legend-cell span {
    display: none;
  }
  .legend-strip {
    width: 100%;
    padding: 7px 10px;
  }
  .legend-title {
    display: none;
  }
}

/* ============================================================
   Station rail (left)
   ============================================================ */

.rail {
  position: absolute;
  top: 76px;
  left: 14px;
  bottom: 26px;
  z-index: 10;
  width: 322px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition:
    transform 0.35s cubic-bezier(0.3, 0.8, 0.3, 1),
    opacity 0.3s ease;
}

.rail[data-open='false'] {
  transform: translateX(-116%);
  opacity: 0;
  pointer-events: none;
  visibility: hidden;
  transition:
    transform 0.35s cubic-bezier(0.3, 0.8, 0.3, 1),
    opacity 0.3s ease,
    visibility 0s linear 0.35s;
}

.rail-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 14px 10px;
}

.rail-head h2 {
  flex: 1;
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.rail-head .kicker {
  display: block;
  margin-top: 2px;
}

.rail-tools {
  display: flex;
  gap: 8px;
  padding: 0 14px 10px;
}

.rail-tools .field {
  flex: 1;
}

.rail-sort {
  flex: 0 0 auto;
}

.rail-list {
  flex: 1;
  overflow-y: auto;
  padding: 2px 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.rail-row {
  display: grid;
  grid-template-columns: 26px 1fr auto;
  align-items: center;
  gap: 9px;
  width: 100%;
  padding: 8px 9px;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;
}

.rail-row:hover,
.rail-row[data-active='true'] {
  background: rgba(36, 48, 74, 0.45);
  border-color: var(--hairline);
}

.rail-rank {
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: var(--text-faint);
  text-align: right;
}

.rail-name {
  min-width: 0;
}

.rail-name strong {
  display: block;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rail-name .rail-bar {
  margin-top: 5px;
  height: 3px;
  border-radius: 99px;
  background: rgba(148, 163, 197, 0.14);
  overflow: hidden;
}

.rail-name .rail-bar i {
  display: block;
  height: 100%;
  border-radius: 99px;
  background: var(--row-color);
}

.rail-empty {
  padding: 28px 16px;
  text-align: center;
  color: var(--text-faint);
  font-size: 13px;
}

@media (max-width: 700px) {
  .rail {
    top: auto;
    left: 10px;
    right: 10px;
    bottom: 10px;
    width: auto;
    height: min(62dvh, 520px);
  }
  .rail[data-open='false'] {
    transform: translateY(112%);
  }
}

/* ============================================================
   Station detail panel (right)
   ============================================================ */

.detail {
  position: absolute;
  top: 76px;
  right: 14px;
  bottom: 26px;
  z-index: 20;
  width: 360px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition:
    transform 0.38s cubic-bezier(0.3, 0.8, 0.3, 1),
    opacity 0.3s ease;
}

.detail[data-open='false'] {
  transform: translateX(116%);
  opacity: 0;
  pointer-events: none;
  visibility: hidden;
  transition:
    transform 0.38s cubic-bezier(0.3, 0.8, 0.3, 1),
    opacity 0.3s ease,
    visibility 0s linear 0.38s;
}

.detail-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.detail-head {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.detail-head h2 {
  flex: 1;
  margin: 3px 0 0;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.01em;
}

.detail-hero {
  display: flex;
  align-items: center;
  gap: 18px;
}

.detail-hero-num {
  font-family: var(--font-mono);
  font-size: 52px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: -0.03em;
}

.detail-hero-cat {
  font-size: 13px;
  font-weight: 700;
}

.detail-hero-msg {
  margin-top: 3px;
  font-size: 12px;
  color: var(--text-dim);
  line-height: 1.45;
}

.detail-section h3 {
  margin: 0 0 10px;
  font-family: var(--font-mono);
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--text-faint);
}

.pollutant-row {
  display: grid;
  grid-template-columns: 52px 1fr 40px;
  align-items: center;
  gap: 10px;
  padding: 5px 0;
}

.pollutant-row .p-name {
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 5px;
}

.pollutant-row .p-driver {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--live);
  box-shadow: 0 0 8px 1px rgba(94, 234, 212, 0.7);
}

.pollutant-row .p-track {
  height: 5px;
  border-radius: 99px;
  background: rgba(148, 163, 197, 0.14);
  overflow: hidden;
}

.pollutant-row .p-track i {
  display: block;
  height: 100%;
  border-radius: 99px;
  background: var(--row-color);
  transition: width 0.6s cubic-bezier(0.3, 0.8, 0.3, 1);
}

.pollutant-row .p-val {
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 600;
  text-align: right;
  color: var(--text-dim);
}

.detail-meta {
  display: grid;
  gap: 4px;
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: var(--text-faint);
}

.detail-meta a {
  color: var(--text-dim);
}

.detail-loading {
  display: grid;
  gap: 8px;
  padding: 8px 0;
}

.detail-loading i {
  height: 10px;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    rgba(148, 163, 197, 0.08),
    rgba(148, 163, 197, 0.2),
    rgba(148, 163, 197, 0.08)
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s linear infinite;
}

@keyframes shimmer {
  from {
    background-position: 200% 0;
  }
  to {
    background-position: -200% 0;
  }
}

.detail-error {
  font-size: 12.5px;
  color: #ffb3ba;
}

@media (max-width: 700px) {
  .detail {
    top: auto;
    left: 10px;
    right: 10px;
    bottom: 10px;
    width: auto;
    height: min(68dvh, 560px);
  }
  .detail[data-open='false'] {
    transform: translateY(112%);
  }
}

/* ============================================================
   Forecast chart
   ============================================================ */

.forecast-svg {
  display: block;
  width: 100%;
  height: auto;
}

.forecast-caption {
  margin-top: 6px;
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--text-faint);
}

/* ============================================================
   Insights view
   ============================================================ */

.insights {
  position: absolute;
  inset: 0;
  overflow-y: auto;
  overflow-x: hidden;
  background:
    radial-gradient(
      1100px 500px at 85% -5%,
      rgba(69, 214, 161, 0.07),
      transparent 60%
    ),
    radial-gradient(
      900px 600px at -10% 20%,
      rgba(176, 126, 242, 0.06),
      transparent 55%
    ),
    var(--bg);
}

.insights-inner {
  max-width: 1180px;
  margin: 0 auto;
  padding: 96px 22px 80px;
  display: grid;
  gap: 16px;
}

@media (max-width: 700px) {
  .insights-inner {
    padding: 84px 12px 60px;
  }
}

.card {
  background: rgba(12, 18, 32, 0.66);
  border: 1px solid var(--hairline);
  border-radius: var(--radius-lg);
  padding: 22px;
  animation: card-rise 0.6s cubic-bezier(0.2, 0.8, 0.3, 1) both;
}

.card:nth-child(2) {
  animation-delay: 0.06s;
}
.card:nth-child(3) {
  animation-delay: 0.12s;
}
.card:nth-child(4) {
  animation-delay: 0.18s;
}
.card:nth-child(5) {
  animation-delay: 0.24s;
}
.card:nth-child(6) {
  animation-delay: 0.3s;
}

@keyframes card-rise {
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

.card h2 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.card .card-sub {
  margin: 4px 0 0;
  font-size: 12.5px;
  color: var(--text-dim);
  line-height: 1.5;
}

.card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
}

/* Hero */
.ins-hero {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 34px;
  align-items: center;
}

@media (max-width: 820px) {
  .ins-hero {
    grid-template-columns: 1fr;
    justify-items: center;
    text-align: center;
  }
}

.ins-hero-copy {
  display: grid;
  gap: 12px;
  max-width: 620px;
}

.ins-hero-copy h1 {
  margin: 0;
  font-size: clamp(26px, 4vw, 38px);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.12;
}

.ins-hero-copy h1 em {
  font-style: normal;
  color: var(--hero-color);
}

.ins-hero-copy p {
  margin: 0;
  font-size: 14px;
  color: var(--text-dim);
  line-height: 1.6;
}

.ins-hero-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 22px;
  margin-top: 6px;
}

@media (max-width: 820px) {
  .ins-hero-stats {
    justify-content: center;
  }
}

.ins-stat {
  display: grid;
  gap: 3px;
}

.ins-stat strong {
  font-family: var(--font-mono);
  font-size: 20px;
  font-weight: 600;
  line-height: 1;
}

.ins-stat span {
  font-family: var(--font-mono);
  font-size: 9.5px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-faint);
}

/* Gauge */
.gauge {
  position: relative;
  display: grid;
  place-items: center;
}

.gauge svg {
  display: block;
}

.gauge-center {
  position: absolute;
  display: grid;
  justify-items: center;
  gap: 2px;
}

.gauge-num {
  font-family: var(--font-mono);
  font-size: 54px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: -0.04em;
}

.gauge-label {
  font-family: var(--font-mono);
  font-size: 9.5px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--text-faint);
}

.gauge-cat {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 700;
}

/* Two/three-column rows */
.ins-grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

@media (max-width: 900px) {
  .ins-grid-2 {
    grid-template-columns: 1fr;
  }
}

/* Histogram */
.histo {
  width: 100%;
}

.histo svg {
  display: block;
  width: 100%;
  height: auto;
}

.histo-bar {
  transition: opacity 0.15s ease;
  cursor: default;
}

.histo:hover .histo-bar:not(:hover) {
  opacity: 0.55;
}

/* Category breakdown */
.catbar {
  display: flex;
  height: 14px;
  border-radius: 99px;
  overflow: hidden;
  background: rgba(148, 163, 197, 0.1);
  margin-bottom: 16px;
}

.catbar i {
  display: block;
  height: 100%;
  min-width: 0;
  transition: width 0.7s cubic-bezier(0.3, 0.8, 0.3, 1);
}

.cat-rows {
  display: grid;
  gap: 3px;
}

.cat-row {
  display: grid;
  grid-template-columns: 13px 1fr auto auto;
  align-items: center;
  gap: 10px;
  padding: 7px 9px;
  border-radius: var(--radius-sm);
  border: 1px solid transparent;
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;
}

.cat-row:hover,
.cat-row[data-active='true'] {
  background: rgba(36, 48, 74, 0.45);
  border-color: var(--hairline);
}

.cat-row .dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--row-color);
  box-shadow: 0 0 10px 0 var(--row-color);
}

.cat-row .cat-name {
  font-size: 12.5px;
  font-weight: 600;
}

.cat-row .cat-range {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--text-faint);
}

.cat-row .cat-count {
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 600;
  color: var(--text-dim);
}

/* Leaderboards */
.board {
  display: grid;
  gap: 4px;
}

.board-row {
  display: grid;
  grid-template-columns: 22px 1fr auto;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 7px 9px;
  border-radius: var(--radius-sm);
  border: 1px solid transparent;
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;
}

.board-row:hover,
.board-row[data-active='true'] {
  background: rgba(36, 48, 74, 0.45);
  border-color: var(--hairline);
}

.board-rank {
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: var(--text-faint);
  text-align: right;
}

.board-name {
  min-width: 0;
}

.board-name strong {
  display: block;
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.board-bar {
  margin-top: 5px;
  height: 3px;
  border-radius: 99px;
  background: rgba(148, 163, 197, 0.12);
  overflow: hidden;
}

.board-bar i {
  display: block;
  height: 100%;
  border-radius: 99px;
  background: var(--row-color);
  transition: width 0.7s cubic-bezier(0.3, 0.8, 0.3, 1);
}

/* Spotlight (selected station inside insights) */
.spotlight {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: 26px;
}

@media (max-width: 900px) {
  .spotlight {
    grid-template-columns: 1fr;
  }
}

.spot-aside {
  display: grid;
  gap: 14px;
  align-content: start;
}

.spot-title {
  font-size: 19px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.25;
  margin: 0;
}

.spot-reading {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.spot-reading strong {
  font-family: var(--font-mono);
  font-size: 44px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: -0.03em;
}

.spot-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* Footer strip */
.ins-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 22px;
  padding: 16px 22px;
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: var(--text-faint);
}

.ins-footer a {
  color: var(--text-dim);
}

.ins-footer .spacer {
  flex: 1;
}

/* ============================================================
   Footer attribution chip (map view)
   ============================================================ */

.credits {
  position: absolute;
  right: 12px;
  bottom: 4px;
  z-index: 30;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 10.5px;
  color: var(--text-faint);
  background: rgba(6, 10, 18, 0.6);
}

.credits button,
.credits a {
  border: 0;
  background: transparent;
  padding: 0;
  font: inherit;
  color: var(--text-dim);
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}

@media (max-width: 700px) {
  .credits {
    bottom: auto;
    top: 58px;
    right: 10px;
  }
}

/* ============================================================
   Modal (legal)
   ============================================================ */

.modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2500;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(3, 5, 10, 0.7);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  animation: fade-in 0.25s ease;
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal {
  width: min(760px, 100%);
  max-height: 82dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: card-rise 0.35s cubic-bezier(0.2, 0.8, 0.3, 1);
}

.modal-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 20px 12px;
  border-bottom: 1px solid var(--hairline);
}

.modal-head h2 {
  flex: 1;
  margin: 0;
  font-size: 16px;
  font-weight: 700;
}

.modal-body {
  overflow-y: auto;
  padding: 18px 20px 26px;
  font-size: 13px;
  color: var(--text-dim);
  line-height: 1.65;
}

.modal-body h3 {
  margin: 20px 0 6px;
  font-size: 13.5px;
  color: var(--text);
}

.modal-body h3:first-child {
  margin-top: 0;
}

.modal-body a {
  color: var(--focus);
}

/* Message state (no data etc.) */
.state-msg {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 40px;
  text-align: center;
  color: var(--text-dim);
  font-size: 15px;
  z-index: 2;
}
